import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, ButtonGroup, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import {
  FulfillmentAdminClient,
  INBOUND_STATUS,
  INBOUND_STATUSES,
  inboundStatusLabel,
} from '../../../infrastructure/FulfillmentAdminClient';
import type {
  CreateInboundRequestLine,
  CreateInboundRequestRequest,
  InboundComment,
  CreateInboundRequestResponse,
  EditInboundRequestRequest,
  InboundRequestSource,
  InboundRequestSummary,
} from '../../../infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '../../../config/ApiRouting';
import { ROUTES } from '../../../routes/routes';
import { kstDay } from '@/utils/fulfillmentTime';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import {
  formatBulkResult,
  NOT_ATTEMPTED,
  unconfirmedSequentialResult,
} from '@/pages/fulfillment/components/bulkResult';
import {
  CATALOG_SEARCH_MIN_LENGTH,
  catalogHitLabel,
  findCatalogHits,
} from '@/pages/fulfillment/components/catalogSearch';
import type { CatalogHit } from '@/pages/fulfillment/components/catalogSearch';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { AttemptKeys } from '@/utils/attemptKeys';
import { AddTrackingModal } from './components/AddTrackingModal';
import { PickedProductStock } from './components/PickedProductStock';
import { InboundCommentThread } from '@/pages/fulfillment/components/InboundCommentThread';
import { MentionWarning } from '@/pages/fulfillment/components/MentionWarning';
import { useMentionSuggest } from '@/pages/fulfillment/components/MentionSuggest';
import { CommentIcon, PencilIcon, PlusIcon, RefreshIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../ultimateDesign.css';
import './inboundRequests.css';

interface DraftLine {
  sku: string;
  qtyExpected: string;
  unitCost: string;
  /** Optional: what the goods are expected to sell for, blank while nobody has priced them. */
  jitPrice: string;
  isFoc: boolean;
  /** Captured when the line was picked and never re-read; an edit carries it through untouched. */
  brand?: string;
  productName?: string;
}

/** The product whose stock panel is shown; `pick` counts picks so picking the same product again re-reads it. */
interface InspectedPick {
  product: CatalogHit;
  pick: number;
}

/** One draft's idempotency key, bound to the request and the words it was minted for. */
interface CommentDraftKey {
  docId: string;
  text: string;
  key: string;
}

/** The request being corrected: its identity, which an edit never moves, and the number it already holds. */
/** What a stale write answers with: the version that won, and who wrote it. */
interface StaleWriteBody {
  code?: string;
  changedBy?: string;
}

interface EditTarget {
  docId: string;
  inboundNo: string;
  /** Every waybill the request already carries, oldest first; empty while none is registered. */
  trackings: string[];
  /** The version the form was built on, sent back so a write over someone else's is refused. */
  updatedAt: string | null;
}

/** How each status answers the row actions, in the spec's own words (BR-32). */
interface RowActionGate {
  editable: boolean;
  cancellable: boolean;
  editTitle: string;
  cancelTitle: string;
}

interface SourceOption {
  value: InboundRequestSource;
  label: string;
  /** What the route means on the floor; the badge alone does not say it. */
  detail: string;
  badge: string;
}

interface AdminActionErrorBody {
  message?: string | string[];
}

/** All is the unfiltered view, not a status; the rest follow the request's own lifecycle order. */
const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'All', label: 'All' },
  ...INBOUND_STATUSES.map((value) => ({ value, label: inboundStatusLabel(value) })),
];

/** Cancelling states why; 기타 says nothing on its own, so it carries the sentence that does. */
const CANCEL_REASONS = ['구매 취소', '잘못 입력', '기타'];
const REASON_NEEDING_MEMO = '기타';

/** A finished request wears green, a dropped one goes quiet, and anything still owed stays amber. */
const STATUS_TONE: Record<string, { bg: string; text: string }> = {
  [INBOUND_STATUS.RECEIVED]: { bg: 'success', text: 'light' },
  [INBOUND_STATUS.CANCELLED]: { bg: 'secondary', text: 'light' },
};

const SOURCE_OPTIONS: SourceOption[] = [
  {
    value: 'SMART_BUY',
    label: 'Smart Buy',
    detail: 'Stock replenishment · Coupang JIT sourcing (via PH)',
    badge: 'SMART BUY',
  },
  { value: 'WHOLESALE', label: 'Wholesale', detail: 'Bulk wholesale inbound (direct request)', badge: 'WHOLESALE' },
  {
    value: 'PARTNERSHIP',
    label: 'Brand',
    detail: 'Brand sponsorship · marketing stock (direct request)',
    badge: 'BRAND',
  },
  { value: 'ETC', label: 'Other', detail: 'Any other channel — type it in', badge: 'OTHER' },
];

const EMPTY_LINE: DraftLine = { sku: '', qtyExpected: '', unitCost: '', jitPrice: '', isFoc: false };

/**
 * Both row actions exist only while nothing physical has happened: once a unit is counted the request
 * is the denominator of a live count, and a cancelled one is terminal. A shut button says which it is.
 */
const TERMINAL_GATE: RowActionGate = {
  editable: false,
  cancellable: false,
  editTitle: 'Request cancelled — terminal',
  cancelTitle: 'Request cancelled — terminal',
};

const ROW_ACTION_GATE: Record<string, RowActionGate> = {
  [INBOUND_STATUS.REQUESTED]: {
    editable: true,
    cancellable: true,
    editTitle: 'Edit request — REQUESTED only',
    cancelTitle: 'Cancel request — REQUESTED only',
  },
  [INBOUND_STATUS.PARTIAL]: {
    editable: false,
    cancellable: false,
    editTitle: 'Stock already received against this request — a PARTIAL request can’t be edited',
    cancelTitle: 'Stock already received against this request — a PARTIAL request can’t be cancelled',
  },
  [INBOUND_STATUS.RECEIVED]: {
    editable: false,
    cancellable: false,
    editTitle: 'Stock has already moved — an INBOUNDED request can’t be edited',
    cancelTitle: 'Stock has already moved — an INBOUNDED request can’t be cancelled',
  },
  [INBOUND_STATUS.CANCELLED]: TERMINAL_GATE,
};

/** An unknown status is treated as terminal: a row nobody can explain must not offer a write. */
const rowActionGate = (status: string): RowActionGate => ROW_ACTION_GATE[status] ?? TERMINAL_GATE;

const GENERIC_ERROR = 'The request failed. Please try again shortly.';

const failureOf = (err: unknown) => (axios.isAxiosError<AdminActionErrorBody>(err) ? err : undefined);

/** The warehouse reads Seoul dates; slicing a UTC stamp prints the wrong day for a Seoul afternoon. */
const warehouseDate = (utc: string): string => kstDay(utc);

/** What the request declared. */
const expectedOf = (row: InboundRequestSummary): number | null => row.expectedQty ?? null;

/** What the desk counted, or null while nothing has been counted — a zero is not a count [D-h]. */
const countedOf = (row: InboundRequestSummary): number | null =>
  row.countedQty != null && row.countedQty > 0 ? row.countedQty : null;

/** Counting books no stock; the explicit confirm still does, so a counted row says it is still waiting [D-h]. */
const awaitingConfirm = (row: InboundRequestSummary): boolean =>
  countedOf(row) !== null && row.status !== INBOUND_STATUS.RECEIVED && row.status !== INBOUND_STATUS.CANCELLED;

/** Every waybill the request carries, oldest first; a legacy row carries the one it registered. */
const waybillsOf = (row: InboundRequestSummary): string[] =>
  row.trackings?.length ? row.trackings : row.tracking ? [row.tracking] : [];

/** The uppercase route badge the sheet and the scan screen both print. */
const routeBadge = (source: string): string =>
  SOURCE_OPTIONS.find((option) => option.value === source)?.badge ?? source;

/** What one arrival can plausibly hold; ops can move it, but a typo of six digits is not a request [D-k]. */
const QTY_CEILING = 100_000;

const QTY_CEILING_ERROR = `A line cannot expect more than ${QTY_CEILING.toLocaleString('en-US')} units.`;
const QTY_WHOLE_ERROR = 'Quantity must be a whole number of at least 1.';
const UNIT_COST_ERROR = 'Unit cost must be whole KRW — the currency has no sub-unit.';
const UNIT_COST_NEGATIVE_ERROR = 'Unit cost cannot be negative — 0 means free of charge.';
const JIT_PRICE_ERROR = 'JIT price must be whole KRW — the currency has no sub-unit.';
const JIT_PRICE_NEGATIVE_ERROR = 'JIT price cannot be negative — leave it blank if unknown.';
const LINE_FIELDS_ERROR = 'Fix the highlighted line values before saving.';
const OTHER_CHANNEL_ERROR = 'Name the channel this arrival came through.';
const NO_CATALOG_MATCH = 'No product matches that SKU, brand or name.';

/** What is wrong with each typed cell of a line, keyed by the cell; a blank cell is unfinished, not wrong. */
interface LineErrors {
  qtyExpected?: string;
  unitCost?: string;
  jitPrice?: string;
}

/** Why a KRW amount cannot be sent, or undefined when it can; the server takes whole, non-negative won only. */
const krwError = (typed: string, negative: string, fractional: string): string | undefined => {
  if (typed.trim() === '') {
    return undefined;
  }
  const amount = Number(typed);
  if (amount < 0) {
    return negative;
  }
  return Number.isInteger(amount) ? undefined : fractional;
};

/**
 * Why each cell of a line cannot be submitted, in the operator's words, so the reason sits beside the
 * cell rather than only shutting the button [D-k]. The rules are the server's own: whole units from 1
 * to the ceiling, and whole, non-negative KRW for both prices.
 */
const lineErrors = (line: DraftLine): LineErrors => {
  const errors: LineErrors = {};
  if (line.qtyExpected.trim() !== '') {
    const qty = Number(line.qtyExpected);
    if (!Number.isInteger(qty) || qty < 1) {
      errors.qtyExpected = QTY_WHOLE_ERROR;
    } else if (qty > QTY_CEILING) {
      errors.qtyExpected = QTY_CEILING_ERROR;
    }
  }
  const unitCost = krwError(line.unitCost, UNIT_COST_NEGATIVE_ERROR, UNIT_COST_ERROR);
  if (unitCost) {
    errors.unitCost = unitCost;
  }
  const jitPrice = krwError(line.jitPrice, JIT_PRICE_NEGATIVE_ERROR, JIT_PRICE_ERROR);
  if (jitPrice) {
    errors.jitPrice = jitPrice;
  }
  return errors;
};

const hasLineError = (line: DraftLine): boolean => Object.keys(lineErrors(line)).length > 0;

/** A line is only submittable once its quantity and cost are stated — a zero cost counts, blank does not [E-7]. */
const isLineComplete = (line: DraftLine): boolean =>
  line.sku.trim() !== '' && line.qtyExpected.trim() !== '' && line.unitCost.trim() !== '' && !hasLineError(line);

export default function FulfillmentInboundRequestsPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);

  const [tab, setTab] = useState<'list' | 'create'>('list');
  const [statusFilter, setStatusFilter] = useState('All');
  const [requests, setRequests] = useState<InboundRequestSummary[]>([]);
  const [footer, setFooter] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** The list read failing is a state the screen stays in, not an outcome that clears itself [T03]. */
  const [loadError, setLoadError] = useState<string | null>(null);
  // One feedback slot for this screen, the same component every desk screen renders [T03].
  const { toasts, showSuccess, showError, showWarning, showFailure, dismiss } = useFulfillmentToast();
  const trackingBulkRef = useRef(false);
  const [created, setCreated] = useState<CreateInboundRequestResponse | null>(null);
  const [cancelling, setCancelling] = useState<InboundRequestSummary | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMemo, setCancelMemo] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  /** What the current filter holds in the whole ledger, which is never the number of rows on screen. */
  const [ledgerCount, setLedgerCount] = useState<number | null>(null);
  const [paging, setPaging] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [trackingTarget, setTrackingTarget] = useState<InboundRequestSummary[] | null>(null);
  const [trackingRefusal, setTrackingRefusal] = useState<string | null>(null);
  const [thread, setThread] = useState<InboundRequestSummary | null>(null);
  const [comments, setComments] = useState<InboundComment[]>([]);
  const [commentDraft, setCommentDraft] = useState('');
  const noteMention = useMentionSuggest<HTMLInputElement>({ value: commentDraft, onChange: setCommentDraft });
  const [commentError, setCommentError] = useState<string | null>(null);
  // A key belongs to one draft on one request: reusing it for different words is what the server refuses.
  const commentKey = useRef<CommentDraftKey | null>(null);
  // Only the thread still on screen may write into it; a slower answer for another request is dropped.
  const openThreadId = useRef<string | null>(null);
  /** Counts catalog lookups so a slow answer to an abandoned query cannot paint over the current one. */
  const catalogAttemptRef = useRef(0);
  // Register and waybill attach are keyed per ask, so a retry or a double click reaches the server as one write.
  const registerKeys = useRef(new AttemptKeys());
  const registerInFlightRef = useRef(false);
  const trackingKeys = useRef(new AttemptKeys());
  const trackingInFlightRef = useRef(false);
  const cancelInFlightRef = useRef(false);
  const commentInFlightRef = useRef(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);

  const [source, setSource] = useState<InboundRequestSource>('WHOLESALE');
  const [supplier, setSupplier] = useState('');
  const [otherChannel, setOtherChannel] = useState('');
  const [poNo, setPoNo] = useState('');
  const [tracking, setTracking] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogHits, setCatalogHits] = useState<CatalogHit[]>([]);
  const [catalogEmpty, setCatalogEmpty] = useState(false);
  const [duplicateSku, setDuplicateSku] = useState<string | null>(null);
  /** The product last picked from the catalog, whose stock and recent inbound sit above the lines. */
  const [inspected, setInspected] = useState<InspectedPick | null>(null);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    client
      .listInboundRequests({ limit: 50, ...(statusFilter === 'All' ? {} : { status: statusFilter }) })
      .then((res) => {
        if (ignore) {
          return;
        }
        setRequests(res.items);
        setFooter(res.footer ?? {});
        setNextCursor(res.nextCursor ?? null);
        setLedgerCount(res.total ?? null);
        setLoadError(null);
      })
      .catch(() => {
        if (!ignore) {
          setLoadError(GENERIC_ERROR);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [client, statusFilter, reloadToken]);

  /** The next page is appended, never swapped in: paging must not take away the rows being worked through. */
  const loadMore = async () => {
    if (!nextCursor || paging) {
      return;
    }
    setPaging(true);
    try {
      const res = await client.listInboundRequests({
        limit: 50,
        cursor: nextCursor,
        ...(statusFilter === 'All' ? {} : { status: statusFilter }),
      });
      setRequests((current) => [...current, ...res.items]);
      setFooter(res.footer ?? {});
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      showFailure(err, GENERIC_ERROR);
    } finally {
      setPaging(false);
    }
  };

  // A desk coming back to this tab has been away while other people worked the same ledger [D-p].
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setReloadToken((token) => token + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // The chips count the whole ledger, not the page, so All is the sum of the footer buckets.
  const ledgerTotal = INBOUND_STATUSES.reduce((total, status) => total + (footer[status] ?? 0), 0);

  const cancelSubmittable = cancelReason !== '' && (cancelReason !== REASON_NEEDING_MEMO || cancelMemo.trim() !== '');

  const closeCancel = () => {
    setCancelling(null);
    setCancelReason('');
    setCancelMemo('');
  };

  /** Only an open request takes further numbers; adding to a terminal one is blocked [E-28], [BR-34]. */
  const takesTracking = (row: InboundRequestSummary): boolean =>
    row.status === INBOUND_STATUS.REQUESTED || row.status === INBOUND_STATUS.PARTIAL;

  /** A closed request's numbers no longer match a scan, so the list shows them as history rather than live [E-28]. */
  const closedWaybillHint = (row: InboundRequestSummary): string | undefined =>
    row.status === INBOUND_STATUS.CANCELLED
      ? 'Request cancelled — this waybill no longer matches a scan'
      : row.status === INBOUND_STATUS.RECEIVED
        ? 'Request completed — this waybill no longer matches a scan'
        : undefined;

  const toggleSelected = (docId: string) =>
    setSelected((previous) =>
      previous.includes(docId) ? previous.filter((id) => id !== docId) : [...previous, docId],
    );

  const openTracking = (rows: InboundRequestSummary[], bulk = false) => {
    trackingBulkRef.current = bulk;
    setTrackingRefusal(null);
    setTrackingTarget(rows);
  };

  /**
   * Saves one set of numbers against every selected request; the modal stays open on a refusal so a
   * transcribed block is never lost to a retry [E-40].
   */
  const saveTracking = async (numbers: string[]) => {
    if (!trackingTarget || trackingInFlightRef.current) {
      return;
    }
    trackingInFlightRef.current = true;
    setBusy(true);
    setTrackingRefusal(null);
    const writes = trackingTarget.map((row) => ({ row, fingerprint: `${row.docId}:${numbers.join('\n')}` }));
    let processed = 0;
    try {
      for (const { row, fingerprint } of writes) {
        const idempotencyKey = trackingKeys.current.keyFor(fingerprint);
        await client.attachInboundTracking(row.docId, { idempotencyKey, bulk: numbers });
        processed += 1;
      }
      writes.forEach(({ fingerprint }) => trackingKeys.current.release(fingerprint));
      if (!trackingBulkRef.current && writes.length === 1) {
        showSuccess(`Tracking saved for inbound request ${writes[0]!.row.inboundNo}.`);
      }
      setTrackingTarget(null);
      setSelected([]);
      setReloadToken((token) => token + 1);
      // A single-row save belongs to S06; the bulk entry point reports request counts, not tracking counts.
      if (trackingBulkRef.current) {
        showSuccess(formatBulkResult('Add Tracking', processed).text);
      }
    } catch (err) {
      const reason = describeFailure(err, GENERIC_ERROR);
      setTrackingRefusal(reason);
      if (trackingBulkRef.current && axios.isAxiosError(err) && (!err.response || err.response.status >= 500)) {
        const outcome = unconfirmedSequentialResult(
          'Add Tracking',
          writes.map(({ row }) => `Inbound request ${row.inboundNo}`),
          processed,
        );
        showError(outcome.text, outcome.details);
      } else if (trackingBulkRef.current) {
        const outcome = formatBulkResult(
          'Add Tracking',
          processed,
          writes
            .slice(processed + 1)
            .map(({ row }) => ({ item: `Inbound request ${row.inboundNo}`, reason: NOT_ATTEMPTED })),
          [{ item: `Inbound request ${writes[processed]!.row.inboundNo}`, reason }],
        );
        const show = outcome.tone === 'error' ? showError : showWarning;
        show(outcome.text, outcome.details);
      }
    } finally {
      trackingInFlightRef.current = false;
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelling || !cancelSubmittable || cancelInFlightRef.current) {
      return;
    }
    cancelInFlightRef.current = true;
    setBusy(true);
    try {
      await client.cancelInboundRequest(cancelling.docId, {
        reason: cancelReason,
        ...(cancelMemo.trim() ? { memo: cancelMemo.trim() } : {}),
      });
      showSuccess(`Inbound request ${cancelling.inboundNo} cancelled.`);
      closeCancel();
      setReloadToken((token) => token + 1);
    } catch (err) {
      showFailure(err, GENERIC_ERROR);
    } finally {
      cancelInFlightRef.current = false;
      setBusy(false);
    }
  };

  /** Opens the row's thread, reading it only when it is asked for — the list carries the count alone. */
  const openThread = async (row: InboundRequestSummary) => {
    if (thread?.docId === row.docId) {
      openThreadId.current = null;
      setThread(null);
      return;
    }
    openThreadId.current = row.docId;
    setThread(row);
    setComments([]);
    setCommentError(null);
    setCommentDraft('');
    try {
      const detail = await client.getInboundRequest(row.docId);
      if (openThreadId.current !== row.docId) {
        return;
      }
      setComments(detail.comments ?? []);
    } catch (err) {
      if (openThreadId.current !== row.docId) {
        return;
      }
      setCommentError(describeFailure(err, GENERIC_ERROR));
    }
  };

  /**
   * Posts one note. The key is minted per draft so a retry after a network failure is the same note,
   * and the draft survives a refusal so nothing typed is lost to it.
   */
  const postComment = async () => {
    const text = commentDraft.trim();
    if (!thread || text === '' || busy || commentInFlightRef.current) {
      return;
    }
    const docId = thread.docId;
    commentInFlightRef.current = true;
    setBusy(true);
    setCommentError(null);
    try {
      const held = commentKey.current;
      // Retrying the same words after a transport failure must land once; changed words are a new note.
      const draftKey =
        held !== null && held.docId === docId && held.text === text
          ? held
          : { docId, text, key: `${docId}:${Date.now()}:${Math.random().toString(36).slice(2)}` };
      commentKey.current = draftKey;
      const posted = await client.appendInboundComment(docId, { text, idempotencyKey: draftKey.key });
      commentKey.current = null;
      if (openThreadId.current !== docId) {
        return;
      }
      setComments(posted.comments);
      showSuccess('Comment added.');
      setCommentDraft('');
      setReloadToken((token) => token + 1);
    } catch (err) {
      const failure = failureOf(err);
      // A refused key can never succeed again; only an ambiguous failure is worth retrying under the same one.
      if (failure?.response?.status === 409) {
        commentKey.current = null;
      }
      if (openThreadId.current !== docId) {
        return;
      }
      setCommentError(describeFailure(err, GENERIC_ERROR));
    } finally {
      commentInFlightRef.current = false;
      setBusy(false);
    }
  };

  /** Reads the request back and dresses the creation form in its saved values; nothing is written yet. */
  /** Rebuilds the form on the version that won a conflict, so the next save is not stale again. */
  const reopenEdit = async (target: EditTarget) => {
    await openEdit({ docId: target.docId, inboundNo: target.inboundNo } as InboundRequestSummary);
  };

  const openEdit = async (row: InboundRequestSummary) => {
    setBusy(true);
    setCreated(null);
    try {
      const detail = await client.getInboundRequest(row.docId);
      setSource((detail.doc.source as InboundRequestSource) ?? 'WHOLESALE');
      setSupplier(detail.doc.supplier ?? '');
      setPoNo(detail.doc.poNo ?? '');
      setOtherChannel(detail.doc.otherChannel ?? '');
      // Registered numbers are shown from the edit target; the box only ever types a first number.
      setTracking('');
      setExpectedAt(detail.doc.expectedAt ?? '');
      setMemo(detail.doc.memo ?? '');
      setLines(
        detail.lines.map((line) => ({
          sku: line.sku,
          qtyExpected: String(line.qtyExpected),
          unitCost: String(line.unitCost ?? 0),
          // The detail view does not carry the selling price back, so an edit leaves it as it stands.
          jitPrice: '',
          isFoc: line.isFoc === true,
          ...(line.brand != null && { brand: line.brand }),
          ...(line.productName != null && { productName: line.productName }),
        })),
      );
      setEditing({
        docId: row.docId,
        inboundNo: row.inboundNo,
        trackings: waybillsOf(detail.doc),
        updatedAt: detail.doc.updatedAt ?? row.updatedAt ?? null,
      });
      setTab('create');
    } catch (err) {
      showFailure(err, GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  /** Leaves edit mode without a server call and returns the form to a blank sheet (§5.4). */
  const exitEdit = () => {
    setEditing(null);
    setSource('WHOLESALE');
    setSupplier('');
    setOtherChannel('');
    setPoNo('');
    setTracking('');
    setExpectedAt('');
    setMemo('');
    setLines([]);
    setCatalogQuery('');
    setCatalogHits([]);
    setDuplicateSku(null);
    setInspected(null);
    setTab('list');
  };

  /** The create tab always opens on a blank sheet, whether or not a request was being edited. */
  const openBlankRequest = () => {
    exitEdit();
    setTab('create');
  };

  /**
   * One box over sku, brand and product name, because the requester types whichever identifier they
   * happen to know. Nothing is searched until the term is worth a query.
   */
  const searchCatalog = async (term: string) => {
    setCatalogQuery(term);
    setDuplicateSku(null);
    // Lookups resolve out of order, so only the newest one may paint; an older answer is discarded.
    const attempt = (catalogAttemptRef.current += 1);
    setCatalogEmpty(false);
    if (term.trim().length < CATALOG_SEARCH_MIN_LENGTH) {
      setCatalogHits([]);
      return;
    }
    try {
      const found = await findCatalogHits(term);
      if (attempt !== catalogAttemptRef.current) {
        return;
      }
      setCatalogHits(found);
      // A box that answers nothing reads as a box that has not answered yet.
      setCatalogEmpty(found.length === 0);
    } catch {
      // A catalog that will not answer must not block the request; the operator can still pick again.
      if (attempt === catalogAttemptRef.current) {
        setCatalogHits([]);
      }
    }
  };

  /** A picked product becomes a row whose catalog cells are locked: the only correction is to delete it. */
  const pickProduct = (hit: CatalogHit) => {
    // Even a duplicate pick is a question about that product, so its stock is shown either way,
    // and picking it again is how the requester asks for a fresh read.
    setInspected((previous) => ({ product: hit, pick: (previous?.pick ?? 0) + 1 }));
    if (lines.some((line) => line.sku === hit.sku)) {
      // Two rows for one sku would double-count the arrival against the same physical delivery.
      setDuplicateSku(hit.sku);
      return;
    }
    setDuplicateSku(null);
    setLines((previous) => [
      ...previous,
      { ...EMPTY_LINE, sku: hit.sku, brand: hit.brand, productName: hit.productName },
    ]);
  };

  /** The lines exactly as the form now declares them; a sku left out of this set is dropped server-side. */
  const declaredLines = (): CreateInboundRequestLine[] =>
    lines.map((line) => ({
      sku: line.sku.trim(),
      qtyExpected: Number(line.qtyExpected),
      unitCost: Number(line.unitCost),
      ...(line.jitPrice.trim() ? { jitPrice: Number(line.jitPrice) } : {}),
      ...(line.isFoc ? { isFoc: true } : {}),
      ...(line.brand !== undefined && { brand: line.brand }),
      ...(line.productName !== undefined && { productName: line.productName }),
    }));

  const submitEdit = async () => {
    if (!editing) {
      return;
    }
    setBusy(true);
    try {
      const payload: EditInboundRequestRequest = {
        ...(editing.updatedAt ? { expectedUpdatedAt: editing.updatedAt } : {}),
        source,
        supplier: supplier.trim(),
        ...(poNo.trim() ? { poNo: poNo.trim() } : {}),
        ...(source === 'ETC' && otherChannel.trim() ? { otherChannel: otherChannel.trim() } : {}),
        // A number the request already holds is never rewritten from here; further numbers go through Add tracking.
        ...(editing.trackings.length === 0 && tracking.trim() ? { tracking: tracking.trim() } : {}),
        ...(expectedAt.trim() ? { expectedAt: expectedAt.trim() } : {}),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
        lines: declaredLines(),
      };
      const result = await client.editInboundRequest(editing.docId, payload);
      if (result.changed) {
        showSuccess(`The inbound request was updated — ${editing.inboundNo}`);
      }
      exitEdit();
      setReloadToken((token) => token + 1);
    } catch (err) {
      const failure = failureOf(err);
      const body = failure?.response?.data as StaleWriteBody | undefined;
      if (failure?.response?.status === 409 && body?.code === 'CONFLICT') {
        // Someone else moved the request first, so the form has to be rebuilt on the version that won.
        showError(`This request was changed by ${body.changedBy ?? 'someone else'} — reloaded on their version.`);
        await reopenEdit(editing);
        setReloadToken((token) => token + 1);
        return;
      }
      // The values stay on screen so a rejected correction can be copied out or retried [E-98].
      showFailure(err, GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Free of charge and a cost of zero are one fact, so the form keeps them in step [D-j]: ticking FOC
   * writes the zero and locks the field, and typing a zero ticks the box.
   */
  const updateLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) {
          return line;
        }
        const next = { ...line, ...patch };
        if (patch.isFoc !== undefined) {
          return { ...next, unitCost: patch.isFoc ? '0' : next.unitCost };
        }
        if (patch.unitCost !== undefined) {
          return { ...next, isFoc: patch.unitCost.trim() !== '' && Number(patch.unitCost) === 0 };
        }
        return next;
      }),
    );

  const registeredWaybills = editing?.trackings ?? [];
  const channelNamed = source !== 'ETC' || otherChannel.trim() !== '';
  const submittable = supplier.trim() !== '' && channelNamed && lines.length > 0 && lines.every(isLineComplete);

  const submitCreate = async () => {
    // setBusy(true) only shuts the button on the next render, which is a tick too late for a double click.
    if (registerInFlightRef.current) {
      return;
    }
    registerInFlightRef.current = true;
    setBusy(true);
    try {
      const draft: CreateInboundRequestRequest = {
        source,
        supplier: supplier.trim(),
        ...(poNo.trim() ? { poNo: poNo.trim() } : {}),
        ...(source === 'ETC' && otherChannel.trim() ? { otherChannel: otherChannel.trim() } : {}),
        ...(tracking.trim() ? { tracking: tracking.trim() } : {}),
        ...(expectedAt.trim() ? { expectedAt: expectedAt.trim() } : {}),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
        lines: lines.map((line) => ({
          sku: line.sku.trim(),
          qtyExpected: Number(line.qtyExpected),
          unitCost: Number(line.unitCost),
          ...(line.jitPrice.trim() ? { jitPrice: Number(line.jitPrice) } : {}),
          ...(line.isFoc ? { isFoc: true } : {}),
          ...(line.brand !== undefined && { brand: line.brand }),
          ...(line.productName !== undefined && { productName: line.productName }),
        })),
      };
      const result = await registerKeys.current.run(JSON.stringify(draft), (idempotencyKey) =>
        client.createInboundRequest({ ...draft, idempotencyKey }),
      );
      setCreated(result);
      showSuccess(`Inbound request ${result.inboundNo} created.`);
      setReloadToken((token) => token + 1);
      setLines([]);
      setCatalogQuery('');
      setCatalogHits([]);
      setInspected(null);
      setTracking('');
      setExpectedAt('');
      setMemo('');
    } catch (err) {
      showFailure(err, GENERIC_ERROR);
    } finally {
      registerInFlightRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Container fluid className="ff-ultimate-page wms-inbound-requests">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">Inbound Request</h2>
          <div className="text-muted small">Inbound stock — separate from customer orders</div>
        </div>
        <div className="d-flex gap-2">
          {/* The list is only current as of the last read, and another desk has been working the same ledger [D-p]. */}
          <Button
            variant="outline-secondary"
            size="sm"
            aria-label="Refresh the request list"
            disabled={loading}
            onClick={() => setReloadToken((token) => token + 1)}
          >
            <RefreshIcon />
            Refresh
          </Button>
          <Button
            variant={tab === 'list' ? 'outline-secondary' : 'primary'}
            size="sm"
            // A blank sheet has to be blank: entering create while editing would overwrite that request on save.
            onClick={() => (tab === 'list' ? openBlankRequest() : exitEdit())}
          >
            {tab === 'list' ? (
              <>
                <PlusIcon />
                New Inbound Request
              </>
            ) : (
              'Request List'
            )}
          </Button>
          <Link to={ROUTES.fulfillment.orders} className="btn btn-outline-secondary btn-sm">
            Back to orders
          </Link>
        </div>
      </div>

      {/* A read that failed says so until it succeeds; every write reports through the one toast [T03]. */}
      {loadError && (
        <Alert variant="danger" role="alert">
          {loadError}
        </Alert>
      )}

      {editing && (
        <Alert variant="info" className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div>
            <div className="fw-semibold">Editing request {editing.inboundNo}</div>
            <div className="small">
              Saving logs every changed field as an auto-comment (old → new) and notifies the requester. The inbound
              number does not change.
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={exitEdit}>
            Exit edit mode
          </Button>
        </Alert>
      )}

      {created && (
        <Alert variant="success">
          <div className="fw-semibold">The inbound request was created</div>
          <div className="fs-4 font-monospace">{created.inboundNo}</div>
          <Alert.Link as={Link} to={ROUTES.fulfillment.inboundRequestDetail(created.docId)}>
            Open detail
          </Alert.Link>
        </Alert>
      )}

      {tab === 'create' && (
        <Card className="mb-3">
          <Card.Header className="fw-semibold">New request</Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
              <Form.Group controlId="inbound-source" className="w-100">
                <Form.Label className="text-muted small text-uppercase mb-1">Sourcing route</Form.Label>
                {/* Four cards rather than a dropdown: the route decides how the goods are paid for and chased. */}
                <div className="d-flex flex-wrap gap-2">
                  {SOURCE_OPTIONS.map((option) => (
                    <Form.Check
                      key={option.value}
                      type="radio"
                      id={`inbound-source-${option.value}`}
                      name="inbound-source"
                      className={`ff-choice-card border rounded p-2 ${source === option.value ? 'border-primary bg-body-tertiary' : ''}`}
                      checked={source === option.value}
                      onChange={() => setSource(option.value)}
                      label={
                        <span className="d-block">
                          <span className="fw-semibold">{option.label}</span>
                          <span className="d-block text-muted small">{option.detail}</span>
                          <Badge bg="secondary" className="mt-1">
                            {option.badge}
                          </Badge>
                        </span>
                      }
                    />
                  ))}
                </div>
                {source === 'ETC' && (
                  <Form.Group controlId="inbound-other-channel" className="mt-2">
                    <Form.Label className="text-muted small text-uppercase mb-1">Other channel name</Form.Label>
                    <Form.Control
                      size="sm"
                      value={otherChannel}
                      placeholder="Enter channel name"
                      onChange={(e) => setOtherChannel(e.target.value)}
                    />
                  </Form.Group>
                )}
              </Form.Group>
              <Form.Group controlId="inbound-supplier">
                <Form.Label className="text-muted small text-uppercase mb-1">Supplier</Form.Label>
                <Form.Control size="sm" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
                <Form.Text muted>Supplier * — who is shipping the goods</Form.Text>
              </Form.Group>
              <Form.Group controlId="inbound-po-no">
                <Form.Label className="text-muted small text-uppercase mb-1">PO No.</Form.Label>
                <Form.Control size="sm" value={poNo} onChange={(e) => setPoNo(e.target.value)} />
                {/* Procurement assigns this by hand and costing joins on it; without it the two lists cannot be reconciled. */}
                <Form.Text muted>The purchase order this was bought under — leave blank for brand stock</Form.Text>
              </Form.Group>
              <Form.Group controlId="inbound-tracking">
                <Form.Label className="text-muted small text-uppercase mb-1">Tracking No</Form.Label>
                {/* A split shipment carries several numbers; showing only the first hides the rest [T17]. */}
                {registeredWaybills.length > 0 ? (
                  <Form.Control
                    as="textarea"
                    size="sm"
                    className="font-monospace"
                    readOnly
                    rows={registeredWaybills.length}
                    value={registeredWaybills.join('\n')}
                  />
                ) : (
                  <Form.Control size="sm" value={tracking} onChange={(e) => setTracking(e.target.value)} />
                )}
                {/* A number registered here is a live scan target at once; further numbers go through Add tracking. */}
                <Form.Text muted>
                  {registeredWaybills.length > 0
                    ? `${registeredWaybills.length} registered · further numbers are added from the list`
                    : 'Tracking No — optional · can be added later after dispatch'}
                </Form.Text>
              </Form.Group>
              <Form.Group controlId="inbound-expected-at">
                <Form.Label className="text-muted small text-uppercase mb-1">Expected arrival</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={expectedAt}
                  onChange={(e) => setExpectedAt(e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="inbound-memo" className="flex-grow-1">
                <Form.Label className="text-muted small text-uppercase mb-1">Memo (Optional)</Form.Label>
                <Form.Control size="sm" value={memo} onChange={(e) => setMemo(e.target.value)} />
              </Form.Group>
            </div>

            <Form.Group controlId="inbound-catalog-search" className="mb-2">
              <Form.Label className="visually-hidden">Search the catalog</Form.Label>
              <Form.Control
                size="sm"
                value={catalogQuery}
                placeholder="Type any SKU No. · brand · product name → click a suggestion to add a row below"
                onChange={(e) => void searchCatalog(e.target.value)}
              />
              {duplicateSku !== null && (
                <div className="text-danger small mt-1">Already added — edit the quantity on the existing row</div>
              )}
              {catalogEmpty && <div className="text-muted small mt-1">{NO_CATALOG_MATCH}</div>}
              {catalogHits.length > 0 && (
                <div className="border rounded mt-1">
                  {catalogHits.map((hit) => (
                    <Button
                      key={hit.sku}
                      variant="link"
                      size="sm"
                      className="d-block text-start text-decoration-none w-100"
                      onClick={() => pickProduct(hit)}
                    >
                      {catalogHitLabel(hit)} <span className="text-muted">{hit.sku}</span>
                    </Button>
                  ))}
                </div>
              )}
            </Form.Group>

            {inspected && <PickedProductStock key={inspected.pick} client={client} product={inspected.product} />}

            <Table responsive className="mb-2 align-middle lines-table">
              <colgroup>
                <col className="col-sku" />
                <col className="col-brand" />
                <col />
                <col className="col-qty" />
                <col className="col-cost" />
                <col className="col-jit" />
                <col className="col-foc" />
                <col className="col-del" />
              </colgroup>
              <thead className="table-dark">
                <tr>
                  <th>SKU No.</th>
                  <th>Brand</th>
                  <th>Product Name</th>
                  <th>Order Qty</th>
                  <th>Unit Cost (KRW) *</th>
                  <th>JIT Price (KRW)</th>
                  <th>FOC</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((draft, index) => {
                  const errors = lineErrors(draft);
                  return (
                    <tr key={index}>
                      <td>
                        <Form.Group controlId={`inbound-line-sku-${index}`}>
                          <Form.Label className="visually-hidden">SKU</Form.Label>
                          {/* The catalog cells are what the buyer picked; correcting them means picking again. */}
                          <Form.Control size="sm" plaintext readOnly value={draft.sku} />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group controlId={`inbound-line-brand-${index}`}>
                          <Form.Label className="visually-hidden">Brand</Form.Label>
                          <Form.Control size="sm" plaintext readOnly value={draft.brand ?? ''} />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group controlId={`inbound-line-name-${index}`}>
                          <Form.Label className="visually-hidden">Product Name</Form.Label>
                          <Form.Control size="sm" plaintext readOnly value={draft.productName ?? ''} />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group controlId={`inbound-line-qty-${index}`}>
                          <Form.Label className="visually-hidden">Qty</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={1}
                            max={QTY_CEILING}
                            step={1}
                            value={draft.qtyExpected}
                            isInvalid={errors.qtyExpected !== undefined}
                            aria-describedby={errors.qtyExpected ? `inbound-line-qty-${index}-error` : undefined}
                            onChange={(e) => updateLine(index, { qtyExpected: e.target.value })}
                          />
                          <Form.Control.Feedback type="invalid" id={`inbound-line-qty-${index}-error`}>
                            {errors.qtyExpected}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group controlId={`inbound-line-cost-${index}`}>
                          <Form.Label className="visually-hidden">Unit Cost (KRW)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            step={1}
                            value={draft.unitCost}
                            disabled={draft.isFoc}
                            isInvalid={errors.unitCost !== undefined}
                            aria-describedby={errors.unitCost ? `inbound-line-cost-${index}-error` : undefined}
                            onChange={(e) => updateLine(index, { unitCost: e.target.value })}
                          />
                          <Form.Control.Feedback type="invalid" id={`inbound-line-cost-${index}-error`}>
                            {errors.unitCost}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group controlId={`inbound-line-jit-${index}`}>
                          <Form.Label className="visually-hidden">JIT Price (KRW)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            step={1}
                            placeholder="Blank if unknown"
                            value={draft.jitPrice}
                            isInvalid={errors.jitPrice !== undefined}
                            aria-describedby={errors.jitPrice ? `inbound-line-jit-${index}-error` : undefined}
                            onChange={(e) => updateLine(index, { jitPrice: e.target.value })}
                          />
                          <Form.Control.Feedback type="invalid" id={`inbound-line-jit-${index}-error`}>
                            {errors.jitPrice}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </td>
                      <CheckCell>
                        <Form.Check
                          type="checkbox"
                          id={`inbound-line-foc-${index}`}
                          label="FOC"
                          checked={draft.isFoc}
                          onChange={(e) => updateLine(index, { isFoc: e.target.checked })}
                        />
                      </CheckCell>
                      <td className="text-end">
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          disabled={lines.length === 1}
                          onClick={() => {
                            setLines((prev) => prev.filter((_, i) => i !== index));
                            // A dropped row's stock no longer answers anything the sheet is asking.
                            if (inspected?.product.sku === draft.sku) {
                              setInspected(null);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className="d-flex flex-wrap gap-2 align-items-center">
              {editing ? (
                <Button variant="primary" size="sm" disabled={busy || !submittable} onClick={() => void submitEdit()}>
                  Save changes
                </Button>
              ) : (
                <Button variant="primary" size="sm" disabled={busy || !submittable} onClick={() => void submitCreate()}>
                  Register Inbound Request
                </Button>
              )}
              <span className="text-muted small">
                Unit cost is required — a zero and the FOC tick are the same fact, so each sets the other.
              </span>
              {!channelNamed && <span className="text-danger small">{OTHER_CHANNEL_ERROR}</span>}
              {/* The reason sits beside its cell; this only says why the button is shut. */}
              {lines.some(hasLineError) && <span className="text-danger small">{LINE_FIELDS_ERROR}</span>}
            </div>
          </Card.Body>
        </Card>
      )}

      {tab === 'list' && (
        <>
          <Card className="mb-3">
            <Card.Body className="d-flex flex-wrap gap-3 align-items-center">
              <ButtonGroup className="ff-segmented">
                {STATUS_FILTERS.map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={statusFilter === filter.value ? 'primary' : 'outline-secondary'}
                    onClick={() => setStatusFilter(filter.value)}
                  >
                    {/* A tab that does not say how much is behind it makes the operator click to find out. */}
                    {`${filter.label} ${filter.value === 'All' ? ledgerTotal : (footer[filter.value] ?? 0)}`}
                  </Button>
                ))}
              </ButtonGroup>
            </Card.Body>
          </Card>

          <div className="d-flex align-items-center gap-3 mb-2">
            <Button
              variant="outline-dark"
              size="sm"
              disabled={busy || selected.length === 0}
              onClick={() =>
                openTracking(
                  requests.filter((row) => selected.includes(row.docId)),
                  true,
                )
              }
            >
              Bulk add tracking numbers
            </Button>
            <span className="text-muted small">{selected.length} selected</span>
          </div>

          <Card>
            <Card.Header className="fw-semibold">Request list ({requests.length})</Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle req-table">
                <colgroup>
                  <col className="col-check" />
                  <col className="col-no" />
                  <col className="col-po" />
                  <col className="col-route" />
                  <col />
                  <col className="col-sku" />
                  <col className="col-qty" />
                  <col className="col-tracking" />
                  <col className="col-expected" />
                  <col className="col-received" />
                  <col className="col-by" />
                  <col className="col-status" />
                  <col className="col-actions" />
                </colgroup>
                <thead className="table-dark">
                  <tr>
                    <th />
                    <th>Inbound No.</th>
                    <th>PO No.</th>
                    <th>Sourcing Route</th>
                    <th>Brand · Product</th>
                    <th>SKU</th>
                    <th className="text-end">Qty</th>
                    <th>Tracking No</th>
                    <th>Expected arrival</th>
                    <th>Received Date</th>
                    <th>Requested by</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((row) => (
                    <Fragment key={row.docId}>
                      <tr>
                        <CheckCell>
                          {/* A terminal request takes no further numbers, so it offers no way to be picked [E-25]. */}
                          {takesTracking(row) && (
                            <Form.Check
                              type="checkbox"
                              aria-label={`Select ${row.inboundNo}`}
                              checked={selected.includes(row.docId)}
                              onChange={() => toggleSelected(row.docId)}
                            />
                          )}
                        </CheckCell>
                        <td>
                          <Link to={ROUTES.fulfillment.inboundRequestDetail(row.docId)} className="font-monospace">
                            {row.inboundNo}
                          </Link>
                        </td>
                        <td className="font-monospace">{row.poNo ?? <span className="text-muted">—</span>}</td>
                        <td>
                          {/* The badge is how the floor reads the route on the sheet and on the scan screen. */}
                          <Badge bg="secondary">{routeBadge(row.source)}</Badge>
                          {row.otherChannel && <div className="small text-muted">{row.otherChannel}</div>}
                        </td>
                        <td>
                          {row.productName ? (
                            <>
                              {row.brand ? `${row.brand} · ${row.productName}` : row.productName}
                              {/* One request can carry many products; naming only the first would read as the whole of it. */}
                              {(row.lineCount ?? 1) > 1 && (
                                <span className="text-muted small ms-1">+{(row.lineCount ?? 1) - 1} more</span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="font-monospace">
                          {row.sku ?? <span className="text-muted">—</span>}
                          {(row.lineCount ?? 1) > 1 && (
                            <span className="text-muted small ms-1">+{(row.lineCount ?? 1) - 1}</span>
                          )}
                        </td>
                        <td className="text-end">
                          {/* Counted against expected, with the over-receipt named rather than folded in [D-h]. */}
                          {countedOf(row) === null ? (
                            (expectedOf(row) ?? <span className="text-muted">—</span>)
                          ) : (
                            <>
                              <div>{`${countedOf(row)} / ${expectedOf(row) ?? '—'}`}</div>
                              {(row.overReceipt ?? 0) > 0 && (
                                <div className="text-warning small">{`+${row.overReceipt} over`}</div>
                              )}
                              {awaitingConfirm(row) && (
                                <Badge bg="warning" text="dark" className="mt-1">
                                  Counted — awaiting confirm
                                </Badge>
                              )}
                            </>
                          )}
                        </td>
                        <td className="font-monospace">
                          {/* One arrival can come in several parcels; showing one waybill hides the rest [T17]. */}
                          {waybillsOf(row).length > 0 ? (
                            waybillsOf(row).map((waybill) => (
                              <div
                                key={waybill}
                                className={
                                  closedWaybillHint(row) ? 'text-muted text-decoration-line-through' : undefined
                                }
                                title={closedWaybillHint(row)}
                              >
                                {waybill}
                              </div>
                            ))
                          ) : takesTracking(row) ? (
                            <Button variant="outline-primary" size="sm" onClick={() => openTracking([row])}>
                              Add tracking
                            </Button>
                          ) : (
                            <span className="text-muted font-monospace">Unregistered</span>
                          )}
                        </td>
                        <td>{row.expectedAt ?? <span className="text-muted">—</span>}</td>
                        <td>
                          {row.receivedAt ? warehouseDate(row.receivedAt) : <span className="text-muted">—</span>}
                        </td>
                        <td>{row.operator ?? '—'}</td>
                        <td>
                          <Badge
                            bg={STATUS_TONE[row.status]?.bg ?? 'warning'}
                            text={STATUS_TONE[row.status]?.text ?? 'dark'}
                          >
                            {inboundStatusLabel(row.status)}
                          </Badge>
                          {/* The badge already says the receipt was short; the note is only how short. */}
                          {row.status === INBOUND_STATUS.PARTIAL && row.partialNote && (
                            <div className="small text-warning-emphasis mt-1">{row.partialNote}</div>
                          )}
                          {/* A cancelled row explains itself on the list, so nobody opens it to ask why [T05]. */}
                          {row.status === INBOUND_STATUS.CANCELLED && (row.cancelReason || row.cancelledAt) && (
                            <div className="small text-muted mt-1">
                              {[row.cancelReason, row.cancelMemo, row.cancelledAt ? kstDay(row.cancelledAt) : null]
                                .filter((part): part is string => Boolean(part))
                                .join(' · ')}
                            </div>
                          )}
                        </td>
                        <td className="text-end">
                          {/* Both actions stay on every row: a shut button that says why beats a row that offers nothing. */}
                          {(() => {
                            const gate = rowActionGate(row.status);
                            return (
                              <div className="d-flex gap-2 justify-content-end">
                                {/* The thread is the floor's own record of an arrival, so every row carries it — terminal or not. */}
                                <Button size="sm" variant="outline-secondary" onClick={() => void openThread(row)}>
                                  <CommentIcon />
                                  Comments{row.commentCount ? ` ${row.commentCount}` : ''}
                                </Button>
                                <Button
                                  size="sm"
                                  variant={gate.editable ? 'outline-secondary' : 'secondary'}
                                  disabled={!gate.editable || busy}
                                  title={gate.editTitle}
                                  onClick={() => void openEdit(row)}
                                >
                                  <PencilIcon />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant={gate.cancellable ? 'outline-danger' : 'secondary'}
                                  disabled={!gate.cancellable || busy}
                                  title={gate.cancelTitle}
                                  onClick={() => setCancelling(row)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {/* The pane opens under the row whose button was pressed, not after the whole page of rows. */}
                      {thread?.docId === row.docId && (
                        <tr>
                          <td colSpan={13} className="bg-body-tertiary">
                            <div className="fw-semibold mb-2">Notes on {thread.inboundNo}</div>
                            {commentError && (
                              <Alert variant="danger" className="py-2">
                                {commentError}
                              </Alert>
                            )}
                            <InboundCommentThread comments={comments} label={`Notes on ${thread.inboundNo}`} />
                            <div className="d-flex gap-2 align-items-center mt-2">
                              <Form.Group controlId="inbound-comment-draft" className="flex-grow-1">
                                <Form.Label className="visually-hidden">Write a note</Form.Label>
                                <Form.Control
                                  {...noteMention.fieldProps}
                                  size="sm"
                                  value={commentDraft}
                                  placeholder="An @mention notifies that person in the comments channel"
                                  onChange={(e) => setCommentDraft(e.target.value)}
                                  onKeyDown={(event) => {
                                    noteMention.onKeyDown(event);
                                  }}
                                />
                              </Form.Group>
                              <Button
                                size="sm"
                                variant="primary"
                                disabled={busy || commentDraft.trim() === ''}
                                onClick={() => void postComment()}
                              >
                                Post
                              </Button>
                            </div>
                            {noteMention.list}
                            <MentionWarning text={commentDraft} typing={noteMention.typing} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {requests.length === 0 && !loading && (
                    <tr>
                      <td colSpan={13} className="text-center text-muted py-4">
                        No inbound requests.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {loading && (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading inbound requests" />
                </div>
              )}
            </Card.Body>
            <Card.Footer className="text-muted small d-flex justify-content-between align-items-center gap-2">
              <span>
                {`Showing ${requests.length} of ${ledgerCount ?? (statusFilter === 'All' ? ledgerTotal : (footer[statusFilter] ?? 0))} request(s) · Status: ${INBOUND_STATUSES.map(
                  (status) => `${inboundStatusLabel(status)} ${footer[status] ?? 0}`,
                ).join(' · ')}`}
              </span>
              {nextCursor && (
                <Button size="sm" variant="outline-secondary" disabled={paging} onClick={() => void loadMore()}>
                  Load more requests
                </Button>
              )}
            </Card.Footer>
          </Card>
        </>
      )}
      <Modal show={cancelling != null} onHide={closeCancel} centered {...ULTIMATE_MODAL}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel inbound request {cancelling?.inboundNo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* The row stays in the list as the audit trail, and its waybills stop matching a scan. */}
          <p className="text-muted small">
            The request is kept on record as cancelled, and every waybill registered on it stops matching a scan.
          </p>
          <Form.Group controlId="cancel-reason" className="mb-3">
            <Form.Label>Reason *</Form.Label>
            <Form.Select value={cancelReason} onChange={(event) => setCancelReason(event.target.value)}>
              <option value="">Choose a reason…</option>
              {CANCEL_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="cancel-memo">
            <Form.Label>Memo — required when reason is Other</Form.Label>
            <Form.Control value={cancelMemo} onChange={(event) => setCancelMemo(event.target.value)} />
            {cancelReason === REASON_NEEDING_MEMO && (
              <Form.Text className="text-danger">기타 says nothing on its own — write what happened.</Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeCancel}>
            Keep request
          </Button>
          <Button variant="danger" disabled={busy || !cancelSubmittable} onClick={() => void submitCancel()}>
            Confirm cancellation
          </Button>
        </Modal.Footer>
      </Modal>
      {trackingTarget && (
        <AddTrackingModal
          title={
            trackingTarget.length === 1 ? (trackingTarget[0]?.inboundNo ?? '') : `${trackingTarget.length} requests`
          }
          busy={busy}
          refusal={trackingRefusal}
          onClose={() => setTrackingTarget(null)}
          onSave={(numbers) => void saveTracking(numbers)}
        />
      )}
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
    </Container>
  );
}
